export const PENDING_EXERCISES_KEY = 'vf_pending_exercises';

export const GROUP_COLORS = {
  chest: '#E53935',
  back: '#1E88E5',
  shoulders: '#FDD835',
  arms: '#43A047',
  core: '#8E24AA',
  legs: '#FB8C00',
  glutes: '#EC407A',
  calves: '#26C6DA',
};

export const LEGEND = [
  { id: 'chest', label: 'Chest', color: GROUP_COLORS.chest, highlight: ['chest'], select: 'chest' },
  { id: 'back', label: 'Back', color: GROUP_COLORS.back, highlight: ['back'], select: 'back' },
  { id: 'shoulders', label: 'Shoulders', color: GROUP_COLORS.shoulders, highlight: ['shoulders'], select: 'shoulders' },
  { id: 'arms', label: 'Arms', color: GROUP_COLORS.arms, highlight: ['biceps', 'triceps', 'forearms'], select: 'biceps' },
  { id: 'core', label: 'Core', color: GROUP_COLORS.core, highlight: ['abs'], select: 'abs' },
  { id: 'legs', label: 'Legs', color: GROUP_COLORS.legs, highlight: ['quads', 'hamstrings'], select: 'quads' },
  { id: 'glutes', label: 'Glutes', color: GROUP_COLORS.glutes, highlight: ['glutes'], select: 'glutes' },
  { id: 'calves', label: 'Calves', color: GROUP_COLORS.calves, highlight: ['calves'], select: 'calves' },
];

const ex = (name, type, bodyPart, equipment, target, instructions) => ({
  id: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
  name,
  type,
  bodyPart,
  equipment,
  target,
  gifUrl: null,
  instructions,
});

export const MUSCLES = {
  chest: {
    id: 'chest',
    name: 'Chest',
    group: 'Chest Muscle Group',
    color: GROUP_COLORS.chest,
    legendId: 'chest',
    bodyPartFilter: 'chest',
    view: 'front',
    preview: { src: 'front', pos: '50% 22%', zoom: 2.2 },
    description:
      'The pectoralis major is the large fan-shaped muscle of the chest. It drives pressing strength and horizontal adduction of the arm. A developed chest improves pushing power and upper-body thickness.',
    functions: [
      'Horizontal adduction (pressing)',
      'Shoulder flexion (front raises / incline press)',
      'Internal rotation of the upper arm',
    ],
    exercises: [
      ex('Barbell Bench Press', 'Primary', 'chest', 'barbell', 'pectorals',
        ['Lie on a flat bench with eyes under the bar', 'Grip slightly wider than shoulders', 'Lower the bar to mid-chest with elbows ~45°', 'Press up until arms lock out']),
      ex('Incline Dumbbell Press', 'Primary', 'chest', 'dumbbell', 'pectorals',
        ['Set a bench to 30–45°', 'Start with dumbbells at the shoulders', 'Press up and slightly together', 'Lower under control to the stretch']),
      ex('Cable Fly', 'Isolation', 'chest', 'cable', 'pectorals',
        ['Set cables at chest height', 'Step forward with a soft elbow bend', 'Bring hands together in an arc', 'Squeeze the chest, then open slowly']),
      ex('Push-Up', 'Compound', 'chest', 'body weight', 'pectorals',
        ['Start in a high plank, hands under shoulders', 'Lower chest toward the floor', 'Keep a straight line from head to heels', 'Press back to the start']),
    ],
    tips: [
      'Retract and depress the shoulder blades before each press.',
      'Use a full range of motion — stretch at the bottom, squeeze at the top.',
      'Don’t bounce the bar off your chest.',
      'Pair flat and incline work for complete pec development.',
    ],
  },
  back: {
    id: 'back',
    name: 'Back',
    group: 'Lat & Mid-Back Group',
    color: GROUP_COLORS.back,
    legendId: 'back',
    bodyPartFilter: 'back',
    view: 'back',
    preview: { src: 'back', pos: '50% 28%', zoom: 2.2 },
    description:
      'The latissimus dorsi and mid-back muscles create the V-taper and stabilize the spine during pulls. A strong back improves posture, deadlift lockout, and every pulling movement.',
    functions: [
      'Shoulder adduction and extension (pulldowns, rows)',
      'Scapular retraction (squeezing the shoulder blades)',
      'Spinal stability during loaded pulls',
    ],
    exercises: [
      ex('Pull-Up', 'Primary', 'back', 'body weight', 'lats',
        ['Hang from a bar with a shoulder-width grip', 'Pull until the chin clears the bar', 'Lead with the chest, not the chin', 'Lower fully under control']),
      ex('Barbell Row', 'Primary', 'back', 'barbell', 'upper back',
        ['Hinge to ~45° with a flat back', 'Pull the bar to the lower ribs', 'Squeeze the shoulder blades together', 'Lower without rounding the spine']),
      ex('Lat Pulldown', 'Primary', 'back', 'cable', 'lats',
        ['Grip the bar wide, sit tall', 'Pull to the upper chest', 'Drive elbows down and back', 'Control the return to a full stretch']),
      ex('Seated Cable Row', 'Isolation', 'back', 'cable', 'traps',
        ['Sit with a tall chest and slight knee bend', 'Pull handles to the abdomen', 'Squeeze the mid-back', 'Reach forward to stretch the lats']),
    ],
    tips: [
      'Initiate pulls with the lats, not the biceps.',
      'Keep the ribcage down — don’t over-arch the lower back.',
      'Full stretch at the top of pulldowns builds width.',
      'Balance horizontal rows with vertical pulls.',
    ],
  },
  shoulders: {
    id: 'shoulders',
    name: 'Shoulders',
    group: 'Deltoid Muscle Group',
    color: GROUP_COLORS.shoulders,
    legendId: 'shoulders',
    bodyPartFilter: 'shoulders',
    view: 'front',
    preview: { src: 'front', pos: '22% 24%', zoom: 2.6 },
    description:
      'The deltoids cap the shoulder and move the arm in every direction. Training all three heads — front, side, and rear — builds round shoulders and protects the joint.',
    functions: [
      'Shoulder abduction (lateral raises)',
      'Overhead pressing (front delts)',
      'Horizontal abduction (rear delt flyes)',
    ],
    exercises: [
      ex('Dumbbell Shoulder Press', 'Primary', 'shoulders', 'dumbbell', 'delts',
        ['Hold dumbbells at shoulder height', 'Press overhead without shrugging', 'Stop just short of locking out hard', 'Lower to the ears under control']),
      ex('Dumbbell Lateral Raise', 'Isolation', 'shoulders', 'dumbbell', 'delts',
        ['Stand tall with a slight elbow bend', 'Raise arms to just below shoulder height', 'Lead with the elbows, not the hands', 'Lower slowly — don’t swing']),
      ex('Face Pull', 'Isolation', 'shoulders', 'cable', 'delts',
        ['Set a rope at face height', 'Pull toward the face, elbows high', 'Externally rotate at the end', 'Control the return']),
      ex('Arnold Press', 'Primary', 'shoulders', 'dumbbell', 'delts',
        ['Start with palms facing you at the shoulders', 'Rotate outward as you press up', 'Finish with palms forward', 'Reverse the path on the way down']),
    ],
    tips: [
      'Train side and rear delts as seriously as the press.',
      'Avoid shrugging — keep traps quiet on raises.',
      'Use lighter loads and strict form for laterals.',
      'Healthy shoulders need pulling volume, not just pressing.',
    ],
  },
  biceps: {
    id: 'biceps',
    name: 'Biceps',
    group: 'Arm Muscle Group',
    color: GROUP_COLORS.arms,
    legendId: 'arms',
    bodyPartFilter: 'upper arms',
    view: 'front',
    preview: { src: 'front', pos: '14% 32%', zoom: 3 },
    description:
      'The biceps brachii is responsible for elbow flexion and helps with forearm rotation. A strong pair of biceps improves pulling strength and overall arm size.',
    functions: [
      'Elbow flexion (curling)',
      'Forearm supination (rotating)',
      'Helps with pulling movements',
    ],
    exercises: [
      ex('Barbell Bicep Curl', 'Primary', 'upper arms', 'barbell', 'biceps',
        ['Stand tall, bar at the thighs, palms up', 'Curl to the shoulders without swinging', 'Squeeze at the top', 'Lower until the arms are nearly straight']),
      ex('Dumbbell Hammer Curl', 'Primary', 'upper arms', 'dumbbell', 'biceps',
        ['Hold dumbbells with a neutral grip', 'Curl toward the shoulders', 'Keep elbows pinned to the sides', 'Lower under control']),
      ex('Incline Dumbbell Curl', 'Isolation', 'upper arms', 'dumbbell', 'biceps',
        ['Lie back on a 30–45° incline bench', 'Let the arms hang for a stretch', 'Curl without moving the elbows forward', 'Pause at the top, then lower slowly']),
      ex('Cable Curl', 'Isolation', 'upper arms', 'cable', 'biceps',
        ['Stand facing a low cable with a bar or handle', 'Curl against constant tension', 'Keep the upper arms still', 'Don’t let the weight stack slam down']),
    ],
    tips: [
      'Focus on controlled movement.',
      'Use a full range of motion.',
      'Don’t swing the weight.',
      'Pair with back exercises for balanced development.',
    ],
  },
  triceps: {
    id: 'triceps',
    name: 'Triceps',
    group: 'Arm Muscle Group',
    color: GROUP_COLORS.arms,
    legendId: 'arms',
    bodyPartFilter: 'upper arms',
    view: 'back',
    preview: { src: 'back', pos: '16% 30%', zoom: 3 },
    description:
      'The triceps brachii makes up most of the upper-arm mass and extends the elbow. Strong triceps unlock bigger presses and a thicker arm from behind.',
    functions: [
      'Elbow extension (pressing and pushdowns)',
      'Stabilizes the elbow in compound presses',
      'Long head assists shoulder extension',
    ],
    exercises: [
      ex('Close-Grip Bench Press', 'Primary', 'upper arms', 'barbell', 'triceps',
        ['Grip the bar just inside shoulder width', 'Lower to the lower chest', 'Keep elbows tucked', 'Press up without flaring']),
      ex('Cable Pushdown', 'Isolation', 'upper arms', 'cable', 'triceps',
        ['Elbows pinned at the sides', 'Extend until the arms are straight', 'Squeeze the triceps at lockout', 'Don’t let the elbows drift forward']),
      ex('Overhead Tricep Extension', 'Isolation', 'upper arms', 'dumbbell', 'triceps',
        ['Hold a dumbbell overhead with both hands', 'Lower behind the head by bending the elbows', 'Keep upper arms vertical', 'Extend back to the top']),
      ex('Tricep Dip', 'Primary', 'upper arms', 'body weight', 'triceps',
        ['Support yourself on parallel bars or a bench', 'Lower until the elbows are near 90°', 'Keep the torso more upright to bias triceps', 'Press back to lockout']),
    ],
    tips: [
      'Lock out fully — that’s where the triceps finish the job.',
      'Tuck the elbows on presses to keep tension on the tris.',
      'Train overhead extensions to hit the long head.',
      'Don’t let the shoulders take over on dips.',
    ],
  },
  forearms: {
    id: 'forearms',
    name: 'Forearms',
    group: 'Arm Muscle Group',
    color: GROUP_COLORS.arms,
    legendId: 'arms',
    bodyPartFilter: 'lower arms',
    view: 'front',
    preview: { src: 'front', pos: '8% 46%', zoom: 3 },
    description:
      'The forearm flexors and extensors control grip, wrist position, and elbow stability. Strong forearms carry over to deadlifts, rows, and every curl you do.',
    functions: [
      'Wrist flexion and extension',
      'Grip strength and endurance',
      'Assists elbow flexion (brachioradialis)',
    ],
    exercises: [
      ex('Dumbbell Hammer Curl', 'Primary', 'lower arms', 'dumbbell', 'forearms',
        ['Neutral grip, elbows tight', 'Curl without swinging', 'Squeeze the brachioradialis', 'Lower slowly']),
      ex('Wrist Curl', 'Isolation', 'lower arms', 'barbell', 'forearms',
        ['Forearms on a bench, wrists over the edge', 'Curl the bar up using only the wrists', 'Lower into a stretch', 'Keep the movement small and controlled']),
      ex('Farmer Carry', 'Compound', 'lower arms', 'dumbbell', 'forearms',
        ['Pick up heavy dumbbells at the sides', 'Walk tall with packed shoulders', 'Crush the handles', 'Set down under control']),
      ex('Reverse Curl', 'Isolation', 'lower arms', 'barbell', 'forearms',
        ['Pronated grip on a bar', 'Curl without letting the elbows drift', 'Control the eccentric', 'Don’t use momentum']),
    ],
    tips: [
      'Train grip directly — it lags behind if you only curl.',
      'Use straps on heavy pulls only after grip work.',
      'Full wrist range of motion, light load.',
      'Dead hangs from a bar build durable forearms.',
    ],
  },
  abs: {
    id: 'abs',
    name: 'Core',
    group: 'Abdominal Muscle Group',
    color: GROUP_COLORS.core,
    legendId: 'core',
    bodyPartFilter: 'waist',
    view: 'front',
    preview: { src: 'front', pos: '50% 40%', zoom: 2.6 },
    description:
      'The rectus abdominis and obliques flex and stabilize the trunk. A strong core transfers force between the upper and lower body and protects the spine under load.',
    functions: [
      'Spinal flexion (crunches, sit-ups)',
      'Anti-extension and anti-rotation (planks, pallof)',
      'Trunk stability during squats and presses',
    ],
    exercises: [
      ex('Plank', 'Primary', 'waist', 'body weight', 'abs',
        ['Forearms on the floor, body in a straight line', 'Brace as if waiting for a punch', 'Don’t let the hips sag or pike', 'Breathe while staying tight']),
      ex('Hanging Leg Raise', 'Primary', 'waist', 'body weight', 'abs',
        ['Hang from a bar with quiet shoulders', 'Raise the legs by curling the pelvis', 'Avoid swinging', 'Lower without losing the brace']),
      ex('Cable Crunch', 'Isolation', 'waist', 'cable', 'abs',
        ['Kneel under a high cable with a rope', 'Crunch the ribcage toward the pelvis', 'Keep the hips relatively still', 'Unroll slowly']),
      ex('Pallof Press', 'Isolation', 'waist', 'cable', 'abs',
        ['Stand sideways to a cable at chest height', 'Press the handle straight out', 'Resist rotation', 'Hold, then return']),
    ],
    tips: [
      'Brace, don’t just suck the stomach in.',
      'Train anti-extension as much as crunches.',
      'Progress load — the core adapts like any other muscle.',
      'Heavy compounds already tax the core; add focused work after.',
    ],
  },
  quads: {
    id: 'quads',
    name: 'Quadriceps',
    group: 'Leg Muscle Group',
    color: GROUP_COLORS.legs,
    legendId: 'legs',
    bodyPartFilter: 'upper legs',
    view: 'front',
    preview: { src: 'front', pos: '38% 62%', zoom: 2.4 },
    description:
      'The quadriceps extend the knee and stabilize it in squats, lunges, and sprints. Building the quads adds leg size and makes every lower-body lift stronger.',
    functions: [
      'Knee extension (squats, leg press, extensions)',
      'Hip flexion (rectus femoris)',
      'Knee stability during gait and jumps',
    ],
    exercises: [
      ex('Barbell Back Squat', 'Primary', 'upper legs', 'barbell', 'quads',
        ['Bar on the upper back, feet about shoulder width', 'Sit down and slightly back', 'Keep the chest up and knees tracking the toes', 'Drive up through mid-foot']),
      ex('Leg Press', 'Primary', 'upper legs', 'leverage machine', 'quads',
        ['Feet mid-platform, shoulder width', 'Lower until the thighs are near the torso', 'Don’t let the lower back round', 'Press through the whole foot']),
      ex('Walking Lunge', 'Compound', 'upper legs', 'body weight', 'quads',
        ['Step forward and drop the back knee', 'Front shin stays fairly vertical', 'Push through the front heel to stand', 'Alternate legs']),
      ex('Leg Extension', 'Isolation', 'upper legs', 'leverage machine', 'quads',
        ['Align the pad with the ankles', 'Extend until the knees are straight', 'Squeeze the quads at the top', 'Lower slowly to a deep bend']),
    ],
    tips: [
      'Depth matters — hit at least parallel on squats.',
      'Keep knees in line with the toes; don’t cave inward.',
      'A slightly forward knee track biases the quads.',
      'Don’t bounce out of the hole — own the bottom position.',
    ],
  },
  hamstrings: {
    id: 'hamstrings',
    name: 'Hamstrings',
    group: 'Leg Muscle Group',
    color: GROUP_COLORS.legs,
    legendId: 'legs',
    bodyPartFilter: 'upper legs',
    view: 'back',
    preview: { src: 'back', pos: '50% 68%', zoom: 2.4 },
    description:
      'The hamstrings flex the knee and extend the hip. They balance the quads, protect the knees, and drive the top half of deadlifts and sprints.',
    functions: [
      'Hip extension (Romanian deadlifts)',
      'Knee flexion (leg curls)',
      'Deceleration and sprint mechanics',
    ],
    exercises: [
      ex('Romanian Deadlift', 'Primary', 'upper legs', 'barbell', 'hamstrings',
        ['Start standing, bar at the thighs', 'Hinge at the hips with a soft knee bend', 'Keep the bar close and the back flat', 'Drive the hips forward to stand']),
      ex('Lying Leg Curl', 'Isolation', 'upper legs', 'leverage machine', 'hamstrings',
        ['Hips glued to the pad', 'Curl the heels toward the glutes', 'Don’t lift the hips', 'Lower slowly']),
      ex('Nordic Hamstring Curl', 'Isolation', 'upper legs', 'body weight', 'hamstrings',
        ['Anchor the ankles and start kneeling tall', 'Lower the torso forward as slowly as possible', 'Catch with the hands if needed', 'Pull back to the start']),
      ex('Good Morning', 'Primary', 'upper legs', 'barbell', 'hamstrings',
        ['Bar on the upper back, soft knees', 'Hinge until the torso is near parallel', 'Feel the stretch in the hamstrings', 'Return by squeezing the glutes']),
    ],
    tips: [
      'Hinge from the hips — don’t squat the RDL.',
      'Train both hip-extension and knee-flexion patterns.',
      'Control the eccentric; hamstrings hate rushed reps.',
      'Pair with quad work so the knee stays balanced.',
    ],
  },
  glutes: {
    id: 'glutes',
    name: 'Glutes',
    group: 'Gluteal Muscle Group',
    color: GROUP_COLORS.glutes,
    legendId: 'glutes',
    bodyPartFilter: 'upper legs',
    view: 'back',
    preview: { src: 'back', pos: '50% 52%', zoom: 2.6 },
    description:
      'The gluteus maximus is the body’s most powerful hip extensor. Strong glutes drive squats, sprints, and posture, and they take stress off the lower back.',
    functions: [
      'Hip extension (thrusts, deadlifts, squats)',
      'Hip abduction and external rotation',
      'Pelvic stability in single-leg work',
    ],
    exercises: [
      ex('Barbell Hip Thrust', 'Primary', 'upper legs', 'barbell', 'glutes',
        ['Upper back on a bench, bar over the hips', 'Drive through the heels to a full lockout', 'Squeeze the glutes hard at the top', 'Lower without losing the brace']),
      ex('Bulgarian Split Squat', 'Primary', 'upper legs', 'dumbbell', 'glutes',
        ['Rear foot elevated, torso slightly forward', 'Drop the back knee toward the floor', 'Front heel stays planted', 'Push up through the front leg']),
      ex('Romanian Deadlift', 'Primary', 'upper legs', 'barbell', 'hamstrings',
        ['Hinge with a flat back', 'Feel the stretch in the glutes and hams', 'Drive the hips through to stand', 'Don’t hyperextend at the top']),
      ex('Glute Bridge', 'Isolation', 'upper legs', 'body weight', 'glutes',
        ['Lie on your back, feet planted', 'Drive the hips up', 'Pause and squeeze', 'Lower without touching all the way down']),
    ],
    tips: [
      'Finish hip extension — squeeze at the top, don’t over-arch the back.',
      'A slight forward torso lean on split squats hits glutes harder.',
      'Full lockout on thrusts, then control the way down.',
      'Don’t let the quads take over; think “push the floor away.”',
    ],
  },
  calves: {
    id: 'calves',
    name: 'Calves',
    group: 'Lower Leg Group',
    color: GROUP_COLORS.calves,
    legendId: 'calves',
    bodyPartFilter: 'lower legs',
    view: 'front',
    preview: { src: 'front', pos: '42% 84%', zoom: 2.8 },
    description:
      'The gastrocnemius and soleus plantar-flex the ankle. Strong calves improve sprinting, jumping, and squat stability, and they complete the look of developed legs.',
    functions: [
      'Ankle plantarflexion (raising onto the toes)',
      'Force absorption when landing',
      'Aids knee stability (gastrocnemius crosses the knee)',
    ],
    exercises: [
      ex('Standing Calf Raise', 'Primary', 'lower legs', 'body weight', 'calves',
        ['Stand on a step with the heels hanging off', 'Rise as high as possible on the toes', 'Pause at the top', 'Lower the heels into a stretch']),
      ex('Seated Calf Raise', 'Isolation', 'lower legs', 'leverage machine', 'calves',
        ['Sit with the pad on the thighs', 'This biases the soleus with a bent knee', 'Full stretch and full squeeze', 'Don’t bounce']),
      ex('Donkey Calf Raise', 'Isolation', 'lower legs', 'body weight', 'calves',
        ['Hinge forward, knees soft', 'Raise the heels through a full range', 'Hold the squeeze', 'Slow negative']),
      ex('Single-Leg Calf Raise', 'Isolation', 'lower legs', 'body weight', 'calves',
        ['Stand on one foot on a step', 'Use a wall only for balance', 'Full range, slow tempo', 'Match both sides']),
    ],
    tips: [
      'Use a full stretch at the bottom — partials stall growth.',
      'Pause at the top; don’t bounce off the stretch.',
      'Train both straight-knee and bent-knee raises.',
      'Higher reps (10–20) work well here, but still add load.',
    ],
  },
};

export const MUSCLE_LIST = Object.values(MUSCLES);

export function readPendingExercises() {
  try {
    const raw = sessionStorage.getItem(PENDING_EXERCISES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function writePendingExercises(items) {
  sessionStorage.setItem(PENDING_EXERCISES_KEY, JSON.stringify(items));
}

export function mergePendingExercises(newItems) {
  const current = readPendingExercises();
  const names = new Set(current.map(e => e.name.toLowerCase()));
  const merged = [...current];
  newItems.forEach(item => {
    if (!names.has(item.name.toLowerCase())) {
      merged.push({
        ...item,
        sets: [{ reps: '', weight: '', logged: false }],
      });
      names.add(item.name.toLowerCase());
    }
  });
  writePendingExercises(merged);
  return merged;
}

// Module-level consume so React Strict Mode remounts don't drop the payload.
let lastConsumed = null;
export function consumePendingExercises() {
  const pending = readPendingExercises();
  if (pending.length) {
    writePendingExercises([]);
    lastConsumed = pending;
    return pending;
  }
  const cached = lastConsumed;
  lastConsumed = null;
  return cached || [];
}
